import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import BaseForm from 'containers/Base/BaseForm';
import {
  initialState,
  generalSection,
  detailedSection,
  validationSchema,
  initialValues,
} from './options';
import {
  transformCreateAssetData,
  transformAssetDataWithType,
} from 'utils/DataTransform/Asset/Form/CreateAsset';
import { isBase64 } from 'utils/checkImage';
import staticText from 'static/text_display.json';
import { ASSET_MANDATORY_IMAGE } from 'utils/constants/assetText';
import { AssetInfo } from 'utils/constants/informationText';
import { translatedKeyValueText } from 'utils/methods/helpers';
import {
  getSecurityCompanyServices,
  getSecurityCompanyId,
  getCompanyAssetCategories,
} from 'reducers/securityCompany';
import { assetGridActions, assetSelectors } from '../';
import useAssetsType from 'containers/Pages/Assets/AssetTypes/Hooks/useAssetsType';
import isEmpty from 'lodash/isEmpty';
import { actions as embeddedFormActions } from 'modules/embeddedForms/reducer';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import { departmentExistInSites } from 'containers/Pages/Tasks/PatrolTemplates/Layouts/PatrolTemplateForm';

const AssetForm = ({
  editMode,
  closeForm,
  userAssignedDepartments,
  persistentStateName,
}) => {
  //-----------------------------Selectors-----------------------------------
  const countries = useSelector(state => state.utils.countries);
  const assetDetails = useSelector(state =>
    assetSelectors.getAssetFormTransformed(state),
  );
  const assetTypeEmbeddedDetails = useSelector(state =>
    assetSelectors.embeddedAssetTypeDetails(state),
  );
  const services = useSelector(state => getSecurityCompanyServices(state));
  const allowableCategories = useSelector(state =>
    getCompanyAssetCategories(state),
  );
  const securityCompanyId = useSelector(state => getSecurityCompanyId(state));
  const siteList = useSites();
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  //----------------------------hooks---------------------------------------
  const [assetCategory, setAssetCategory] = useState('');
  const [type, setType] = useState();
  const [formVals, setFormVals] = useState();
  // const [eventCategory, setEventCategory] = useState('');

  const assetTypeList = useAssetsType();
  //----------------------------Actions-------------------------------------
  const dispatch = useDispatch();
  const dispatchCreateAsset = useCallback(
    ({ asset }) => dispatch(assetGridActions.createRowEntry({ asset })),
    [dispatch],
  );
  const dispatchUpdateAsset = useCallback(
    ({ id, asset }) => dispatch(assetGridActions.updateRowEntry({ id, asset })),
    [dispatch],
  );

  const handleChange = useCallback(
    vals => {
      if (vals.category === '') {
        vals.type = '';
        setAssetCategory('');
      } else {
        setAssetCategory(vals.category);
      }
      if (vals.type) {
        setType(vals.type);
      } else {
        setType();
      }
      if (vals?.departments) {
        setSelectedDepartments(vals?.departments);
        // check if the selected department exists in the selected sites
        // if yes, then don't change anything
        // if no, then remove the selected option from the site select field
        if (!isEmpty(vals.site) && !departmentExistInSites(vals)) {
          vals.site = '';
        }
      }
      if (!isEmpty(assetTypeEmbeddedDetails)) {
        // fill in the asset type
        vals.category = assetTypeEmbeddedDetails.category;
        vals.type = {
          typeName: assetTypeEmbeddedDetails.name,
          id: assetTypeEmbeddedDetails.id,
          handoverRequired: assetTypeEmbeddedDetails.handoverRequired,
          qualificationRequired: assetTypeEmbeddedDetails.qualificationRequired,
        };
        vals.assetTypeId = assetTypeEmbeddedDetails.id;
      }
      setFormVals(vals);
    },
    [assetTypeEmbeddedDetails],
  );

  const onEdit = values => {
    if (!isBase64(values.image)) {
      Reflect.deleteProperty(values, 'image');
    }
    if (values.newAssetType) {
      const transformedData = transformAssetDataWithType(values);
      dispatchUpdateAsset({
        id: assetDetails.others.id.value,
        asset: transformedData,
      });
    } else {
      let assetTypeId = type?.id;
      // So that it does not submit '-' to the type.id to the backend in case of the type is archived
      if (assetTypeId === '-' || !assetTypeId) {
        assetTypeId = values.typeId;
      }
      values.site = siteList.find(site => site.id === values?.site?.id);
      const transformedData = transformCreateAssetData(values, assetTypeId);
      dispatchUpdateAsset({
        id: assetDetails.others.id.value,
        asset: transformedData,
      });
    }
  };

  const onCreate = values => {
    if (values.newAssetType) {
      const transformedData = transformAssetDataWithType(values);
      dispatchCreateAsset({ asset: transformedData });
    } else {
      values.site = siteList.find(site => site.id === values?.site?.id);
      const transformedAssetData = transformCreateAssetData(values, type.id);
      dispatchCreateAsset({ asset: transformedAssetData });
    }
  };

  const checkAssetTypeForImage = () =>
    ASSET_MANDATORY_IMAGE.includes(assetCategory);

  const transformedInitialValues = editMode
    ? initialValues(assetDetails)
    : initialState;

  useEffect(() => {
    if (!isEmpty(assetTypeEmbeddedDetails)) {
      // clear embedded details
      dispatch(embeddedFormActions.clearEmbeddedFormDetails());
    }
    return () => {
      // clear embedded details if not empty
      if (!isEmpty(assetTypeEmbeddedDetails)) {
        dispatch(embeddedFormActions.clearEmbeddedFormDetails());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetTypeEmbeddedDetails]);

  return (
    <>
      <BaseForm
        imageRequired={true}
        multipleImageRequired={true} // This enables multiple image uploads
        isMultipleImageRequired={false} // This puts asterisks to the multi image uploads. You are still required to set its validation in validation schema under surfaces keyword
        edit={editMode}
        initialValues={{
          ...transformedInitialValues,
          ...formVals, // keeps track of newly selected or type values
          ...(!isEmpty(assetTypeEmbeddedDetails) && {
            category: assetTypeEmbeddedDetails.category,
            type: {
              typeName: assetTypeEmbeddedDetails.name,
              id: assetTypeEmbeddedDetails.id,
              handoverRequired: assetTypeEmbeddedDetails.handoverRequired,
              qualificationRequired:
                assetTypeEmbeddedDetails.qualificationRequired,
            },
            assetTypeId: assetTypeEmbeddedDetails.id,
          }),
        }}
        formTitle={
          editMode
            ? staticText.managerial_view.asset.edit_default
            : staticText.managerial_view.asset.create
        }
        buttonText={editMode ? 'Update' : 'Create'}
        closeForm={closeForm}
        onChange={handleChange}
        onSubmit={editMode ? onEdit : onCreate}
        validationSchema={validationSchema(
          assetCategory,
          editMode,
          checkAssetTypeForImage(),
        )}
        generalSection={generalSection({
          allowableCategories,
          assetCategory,
          countries,
          assetTypeList,
          services,
          userAssignedDepartments,
          securityCompanyId,
          siteList,
          selectedDepartments,
        })}
        detailedSection={detailedSection({
          assetCategory,
          type,
          // eventCategory,
          // setEventCategory,
          services,
        })}
        imageType="Establishment"
        hoverText={editMode ? assetDetails.generalInfo.assetName.value : ''}
        saveHoverText={
          editMode
            ? translatedKeyValueText('update_value_title', {
                value: staticText.managerial_view.asset.update,
              })
            : staticText.managerial_view.asset.create
        }
        enableReinitialize={false}
        isImageMandatory={checkAssetTypeForImage()}
        note={AssetInfo.note}
        persistentStateName={persistentStateName}
      />
    </>
  );
};

AssetForm.propTypes = {
  editMode: PropTypes.bool,
  closeForm: PropTypes.func,
  userAssignedDepartments: PropTypes.array,
  persistentStateName: PropTypes.string,
};

export default AssetForm;
